import React, { Component } from 'react';
import '@atlaskit/css-reset';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/core';
import { colors } from '@atlaskit/theme';
import type {
  DropResult,
  DraggableLocation,
  DroppableProvided,
} from 'react-beautiful-dnd';
import Column from './column';
import reorder, { reorderQuoteMap } from './reorder';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import CardService from "../services/card.js";
import ColumnService from "../services/column.js";
import plusImg from "./assets/plus.png";
import {getBoardIdFromURL} from "./../util/common.js";
import { announce } from "./../util/liveRegion.js";

const boardID = "FtyHdinaaSGbqPs8VS6D"; //load from url

const ParentContainer = styled.div`
  height: ${({ height }) => height};
  overflow-x: hidden;
  overflow-y: auto;
`;

const Container = styled.main`
  background-color: white; //TODO: take care of dark mode here
  min-height: 100vh;
  /* like display:flex but will allow bleeding over the window width */
  min-width: 100vw;
  display: inline-flex;
  padding-top: 80px;
`;

const AddColumnButton = styled.button`
  background-color: #f7f5f4;
  border: none;
  height: 40px;
  margin-top: 10px;
  width: 300px;
  line-height: 0;
  outline: none;
  padding: 10px;
  font-family: 'Segoe UI Regular';
  color: #5c6263;
  border-radius: 5px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;
  font-size: 0.8rem;

  &:hover {
    background-color: #eee9e7;
  }
`;

const Icon = styled.img`
  height: ${props => props.height ? props.height : "11"}px;
  display: inline-block;
  padding-right: 5px;
`;

type Props = {|
  initial: QuoteMap,
  withScrollableColumns?: boolean,
  isCombineEnabled?: boolean,
  containerHeight?: string,
  useClone?: boolean,
|};

export default class Board extends Component {
  static defaultProps = {
    isCombineEnabled: false, // enables combining cards
  };

  constructor(props) {
    super(props);
    this.firestoreSubscribtion = null;
    this.state = {
      creatingColumnInProgress: false,
      isDragging: false,
      lastDBResponseCode: this.props.lastDBResponseCode,
      columns: this.props.board.columns,
      ordered: this.getColumnKeysOrderedByIndex() //problem now: db changes cases UI to re-render but this part won't be repeated so the order of columns stay the same.
    };
  }
  
  getColumnKeysOrderedByIndex() {
    const {columns = []} = this.props.DBRepresentationBoardJSON;
 
    function compare( a, b ) {
      if ( columns[a].index < columns[b].index ){
        return -1;
      }
      if ( columns[a].index > columns[b].index ){
        return 1;
      }
      return 0;
    }
    return Object.keys(columns).sort(compare);
  }
  
  onDragStart = (result) => {
    if (result.type === 'COLUMN') {
      this.setState({isDragging: true});
    }
  }

  onDragEnd = (result) => {    
    this.setState({isDragging: false}); 
    // dropped nowhere
    if (!result.destination) {
      return;
    }

    const source: DraggableLocation = result.source;
    const destination: DraggableLocation = result.destination;

    // did not move anywhere - can bail early
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // reordering column
    if (result.type === 'COLUMN') {
      const ordered: string[] = reorder(
        this.state.ordered,
        source.index,
        destination.index,
      );
      try {
        this.updateColumnsIndexesInDB(ordered);
      } catch(ex) {
        console.log("Failed to update sort in db", ex)
      }
      this.setState({
        ordered,
      });

      return;
    }

    const data = reorderQuoteMap({
      quoteMap: this.state.columns,
      source,
      destination,
    });
    try {
      this.updateCardsIndexesInDB(result, data?.quoteMap);
    } catch(ex) {
      console.error("Failed to update sort in db", ex);
    }
    

    // card move between columns or within column ends up here
    this.setState({
      columns: data.quoteMap,
    });
  };

  updateColumnsIndexesInDB = (newColumnsOrder) => {     
    if (!newColumnsOrder && !newColumnsOrder.length) {
      return;
    }
    // reindex columns in this board
    const update = {};
    newColumnsOrder.forEach((columnKey, index) => {
      update[`columns.${columnKey}.index`] = index;
    });
    
    ColumnService.updateWithCustomPayload(this.props.board.boardPublicId, update);
  }

  updateCardsIndexesInDB = (moveMetadata, newColumns) => {     
    if (!moveMetadata && this.props.board.id) {
      return;
    }
    const source = moveMetadata.source;
    const destination = moveMetadata.destination;
    // Case 1 - Card moved within same column
    if (source.droppableId === destination.droppableId) {
      // reindex cards in that column
      const update = {};
      const newlyOrderdCards = newColumns[destination.droppableId] ?? [];
      newlyOrderdCards.forEach((card, index) => {
        update[`columns.${destination.droppableId}.cards.${card.cardId}.index`] = index;
      });
      
      CardService.updateWithCustomPayload(this.props.board.boardPublicId, update);
     }

    if (
      source.droppableId !== destination.droppableId
    ) {
      CardService.moveCardToAnotherColumn(
        this.props.board.boardPublicId,
        moveMetadata.draggableId,
        source,
        destination,
        newColumns
      ); 
    }
  }

  /**
   * The goal here is to keep state in sync with DB data (i.e. parent props)
   * If the state doesn't have the latest data, we need to update it whenever props change
   * because that means the server data changed.
   * Why do we need a state, can't we rely on props then so it's always up to date? 
   * Smart, but without a state, when a user changes order locally, they'll have to wait for data
   * to be written to db and comes back before the UI refelects the change. To perform optimistic
   * UI update, we need the state. So this solution gives us both benifts: optimistic fast UI update +
   * whenever backend data has changed, we update the local state to refelct the changes.
   * 
   * Important guide: https://www.pluralsight.com/guides/how-to-pass-new-server-data-to-react.js-components
   * @param {} preProps 
   * @param {*} prevState 
   */
  componentDidUpdate(preProps, prevState) {
    // if state payload is NOT based on the latest data we got from server, then update state to match DB data.
    /**
     * How this works?
     * In this method our goal is to figure out if state is up to date with props (which represents server data) or not.
     * To do that, I pass a random code from parent component every time it renders.
     * And in this function I check, if the parent prop !== the state in this comopnent, it means the state in 
     * this component is stale and need to be udpated with parent props + update the code in the state becuase
     * once we call this.setState below, this function will be retriggered. This time the code from prop matches
     * the local state since we just updated it, so no further updates necessary and our compoent doesn't enter endless loop.
     * 
     * Important guide: https://www.pluralsight.com/guides/how-to-pass-new-server-data-to-react.js-components
     */
    if (prevState.lastDBResponseCode !== this.props.lastDBResponseCode) {
      this.setState({ordered: this.getColumnKeysOrderedByIndex() });
      this.setState({columns: this.props.board.columns });
      // the line below will prevent the if condition above from populating to true after we change the state here
      // so it breaks the infite loop of updates.
      this.setState({lastDBResponseCode: this.props.lastDBResponseCode});
    }
  }

  onCreateNewColumn = async () => {
    this.setState({creatingColumnInProgress: true}, async () => {
      await ColumnService.create(getBoardIdFromURL());
      this.setState({creatingColumnInProgress: false});
      announce("New column created");
    });
  }

  getColumnTitle = (columnId) => {
    return this.props.DBRepresentationBoardJSON?.columns?.[columnId]?.title ?? "";
  }

  onMoveColumn = (columnId, direction) => {
    const { ordered } = this.state;
    const currentIndex = ordered.indexOf(columnId);
    if (currentIndex === -1) return;
    const newIndex = direction === "left" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= ordered.length) return;

    const newOrdered = reorder(ordered, currentIndex, newIndex);
    this.setState({ ordered: newOrdered });
    try {
      this.updateColumnsIndexesInDB(newOrdered);
    } catch (ex) {
      console.error("Failed to update column order", ex);
    }
    announce(`Column ${this.getColumnTitle(columnId)} moved to position ${newIndex + 1} of ${ordered.length}`);
  }

  onMoveCard = (columnId, cardId, direction) => {
    const { columns, ordered } = this.state;
    const cards = columns[columnId] || [];
    const currentCardIndex = cards.findIndex((c) => c.cardId === cardId);
    if (currentCardIndex === -1) return;

    if (direction === "up" || direction === "down") {
      const newCardIndex = direction === "up" ? currentCardIndex - 1 : currentCardIndex + 1;
      if (newCardIndex < 0 || newCardIndex >= cards.length) return;

      const source = { droppableId: columnId, index: currentCardIndex };
      const destination = { droppableId: columnId, index: newCardIndex };
      const data = reorderQuoteMap({ quoteMap: columns, source, destination });

      this.setState({ columns: data.quoteMap });
      try {
        this.updateCardsIndexesInDB({ source, destination, draggableId: cardId, type: "QUOTE" }, data.quoteMap);
      } catch (ex) {
        console.error("Failed to update card order", ex);
      }
      announce(`Card moved ${direction === "up" ? "up" : "down"} to position ${newCardIndex + 1} in column ${this.getColumnTitle(columnId)}`);
      return;
    }

    if (direction === "previousColumn" || direction === "nextColumn") {
      const columnIndex = ordered.indexOf(columnId);
      const targetColumnIndex = direction === "previousColumn" ? columnIndex - 1 : columnIndex + 1;
      if (targetColumnIndex < 0 || targetColumnIndex >= ordered.length) return;
      const targetColumnId = ordered[targetColumnIndex];
      const targetCards = columns[targetColumnId] || [];
      const destinationIndex = targetCards.length;

      const source = { droppableId: columnId, index: currentCardIndex };
      const destination = { droppableId: targetColumnId, index: destinationIndex };
      const data = reorderQuoteMap({ quoteMap: columns, source, destination });

      this.setState({ columns: data.quoteMap });
      try {
        CardService.moveCardToAnotherColumn(this.props.board.boardPublicId, cardId, source, destination, data.quoteMap);
      } catch (ex) {
        console.error("Failed to move card across columns", ex);
      }
      announce(`Card moved to column ${this.getColumnTitle(targetColumnId)}`);
      return;
    }
  }

  render() {
    const {columns, ordered, creatingColumnInProgress} = this.state;

    const {
      containerHeight,
      useClone,
      isCombineEnabled,
      withScrollableColumns,
      DBRepresentationBoardJSON,
      isFilterActive
    } = this.props;
    const board = (
      <Droppable
        droppableId="board"
        type="COLUMN"
        direction="horizontal"
        ignoreContainerClipping={Boolean(containerHeight)}
        isCombineEnabled={isCombineEnabled}
        isDropDisabled={isFilterActive}
      >
        {(provided: DroppableProvided) => (
          
          <Container id="board-main" tabIndex={-1} ref={provided.innerRef} {...provided.droppableProps} aria-label="Board columns">
            {Object.keys(columns)?.length > 0 && (() => {
              const visibleOrdered = ordered.filter((columnKey) => DBRepresentationBoardJSON.columns[columnKey]); // This filter is useful when user removed a column, and the ordered state still has that column id stale
              return visibleOrdered.map((key: string, index: number) => (
                <Column
                  key={key}
                  index={index}
                  title={DBRepresentationBoardJSON.columns[key].title}
                  columnId={key}
                  cards={columns[key]}
                  mapOfAllTags={this.props.board?.mapOfAllTags}
                  isScrollable={withScrollableColumns}
                  isCombineEnabled={isCombineEnabled}
                  useClone={useClone}
                  isFilterActive={isFilterActive}
                  onMoveColumn={this.onMoveColumn}
                  onMoveCard={this.onMoveCard}
                  isFirstColumn={index === 0}
                  isLastColumn={index === visibleOrdered.length - 1}
                  previousColumnTitle={index > 0 ? DBRepresentationBoardJSON.columns[visibleOrdered[index - 1]]?.title : null}
                  nextColumnTitle={index < visibleOrdered.length - 1 ? DBRepresentationBoardJSON.columns[visibleOrdered[index + 1]]?.title : null}
                />
              ));
            })()}
            {Object.keys(columns)?.length < 5 && this.state.isDragging !== true && 
              <AddColumnButton type="button" aria-label={creatingColumnInProgress ? 'Creating new column' : 'Add new column to board'} onClick={creatingColumnInProgress ? () => {} : this.onCreateNewColumn}><Icon src={plusImg} alt="" aria-hidden="true" /> {creatingColumnInProgress ? "Creating ..." : "Add new column"}</AddColumnButton>}
            {provided.placeholder}            
          </Container> 
        )}
      </Droppable>
    );

    return (
      <React.Fragment>
        <DragDropContext
          onDragEnd={this.onDragEnd}
          onDragStart={this.onDragStart}
          dragHandleUsageInstructions=" "
        >
          {containerHeight ? (
            <ParentContainer height={containerHeight}>{board}</ParentContainer>
          ) : (
            board
          )}
        </DragDropContext>
        <Global
          
        />
      </React.Fragment>
    );
  }
 }
